import React from 'react';
import RouteHead from '../../components/RouteHead/RouteHead';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import './PrivacyPolicyPage.css';
import Footer from '../../components/Footer/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-policy-container">
      <RouteHead path="/privacy-policy" />
      <Navbar />

      <div className="privacy-content">
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-last-updated">Last updated: {new Date().toLocaleDateString()}</p>

        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to VOIA Travel. We are committed to protecting your privacy and ensuring you have a positive experience on our website and in using our products and services. This Privacy Policy explains how we collect, use, and safeguard your information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          
          <h3>2.1 Personal Information</h3>
          <p>Name, email address, and other contact details you provide when joining our waiting list or using our services.</p>

          <h3>2.2 Usage Data</h3>
          <p>Information about how you interact with our website and app, including pages visited, time spent, and features used.</p>

          <h3>2.3 Travel Information</h3>
          <p>Trip details, preferences, and itinerary data that you provide when using our travel planning services. Travel history may also be inferred automatically from photo metadata you allow us to access (see 2.5).</p>

          <h3>2.4 Camera Access</h3>
          <p>Our app may request access to your camera.</p>
          <ul>
            <li>The camera is only activated when you choose to take a photo.</li>
            <li>We do not access the camera in the background.</li>
            <li>We do not capture images without explicit user action.</li>
          </ul>

          <h3>2.5 Photo Library Access and Metadata</h3>
          <p>
            With your permission, our app accesses your device's photo library to read photo
            metadata — including location (GPS) coordinates and date/time information (EXIF data)
            — in order to automatically reconstruct your travel history and the countries you have visited.
          </p>
          <ul>
            <li>This feature runs only after you explicitly grant photo library permission, and you can revoke that permission at any time in your device settings. Revoking permission stops any further collection and processing of your photo metadata.</li>
            <li>The metadata we extract is uploaded to and stored on our servers (Firebase) to build and display your travel history.</li>
            <li>We use this metadata solely to reconstruct your travel history and provide related features — not for advertising or sale.</li>
            <li>To permanently delete the metadata and reconstructed travel history stored on our servers, you can delete your account, which removes this associated data (see "Your Rights" below).</li>
          </ul>
          <p>
            Because GPS metadata is precise location information, we treat it as sensitive personal
            data and process it only with your consent.
          </p>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and improve our travel planning services</li>
            <li>Reconstruct and display your travel history from photo metadata you grant access to</li>
            <li>Send you updates about our product launch and features</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Personalize your experience with our AI travel agent</li>
            <li>Analyze usage patterns to enhance our services</li>
          </ul>
          <p>We use third-party services such as Firebase Authentication, Firestore, and Firebase Storage to securely manage user accounts and store user data, including the photo metadata and reconstructed travel history described in section 2.5.</p>
        </section>

        <section className="privacy-section">
          <h2>4. Data Security</h2>
          <p>
          We implement industry-standard technical and organizational measures to protect your information from unauthorized access, alteration, disclosure, or destruction.
          </p>
          <p>However, no method of data transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
        </section>

        <section className="privacy-section">
          <h2>5. Data Sharing</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
          </p>
          <ul>
            <li>With your explicit consent</li>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and safety</li>
            <li>With service providers who assist us in operating our platform (under strict confidentiality agreements)</li>
          </ul>
          <p>
            <strong>Subscriptions.</strong> If you subscribe to VOIA Premium, the purchase is processed by the Apple App Store or Google Play. We use RevenueCat to manage subscription status and entitlements, which involves sharing an anonymous subscriber identifier and your subscription state with them. We do not receive or store your payment card details: those stay with Apple and Google.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Your Rights</h2>
          <p>Depending on your jurisdiction (including GDPR regions), you have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your information; deleting your account removes your reconstructed travel history and the photo metadata used to create it</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent at any time</li>
            <li>Withdraw photo library access at any time, which stops further collection and processing of photo metadata</li>
          </ul>
          <p>To exercise your rights, contact us at the email below.</p>
        </section>

        <section className="privacy-section">
          <h2>7. Cookies and Tracking</h2>
          <p>
          We may use cookies, analytics tools, and similar technologies to:
          </p>
          <ul>
            <li>Improve user experience</li>
            <li>Understand how users interact with VOIA</li>
            <li>Support marketing and performance optimization</li>
          </ul>
          <p>You can manage cookie settings through your browser.</p>
        </section>

        <section className="privacy-section">
          <h2>8. Third-Party Links</h2>
          <p>
          Our app and website may contain links to third-party websites or services.
          We are not responsible for the privacy practices or content of these external sites.
          </p>
          <p>We encourage you to review their privacy policies.</p>
        </section>

        <section className="privacy-section">
          <h2>9. Children's Privacy</h2>
          <p>
            VOIA Travel is not intended for individuals under the age of 13.
            We do not knowingly collect personal data from children.
            If we learn that a child’s data has been collected, we will take appropriate action to delete it.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time.
            When we do, we will update the “Last updated” date at the top of this page.
            Significant changes may also be communicated via email or in-app notifications.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p className="privacy-contact">
            Email: admin@voiatravel.com<br />
            VOIA Travel
          </p>
        </section>

        <div className="privacy-footer-actions">
          <Link to="/" className="privacy-back-link">← Back to Home</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;

